// @services/clients/DefaultInviteAcceptClient.ts
import { v7 as uuidv7 } from "uuid";
import type { InviteAcceptClient } from "@services/clients/InviteAcceptClient";
import { InviteAcceptRoutes } from "@services/clients/InviteAcceptRoutes";
import type { InviteAcceptRequest } from "@ps-bridge/controllers/inviteAccept";
import type { JwtSessionAction } from "@ps-bridge/controllers/jwtSession";
import type { LoadingErrorInput } from "@ps-bridge/controllers/loading";
import type { TaskEitherTs } from "@ps-bridge/interop";
import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import * as Option from "@utils/Option";
import { toLoadingErrorInput } from "@utils/loadingError";
import {
    ioMap,
    taskEitherChain,
    taskEitherFromIO,
    taskEitherLeft,
    taskEitherOf,
    taskEitherTryCatch,
} from "@utils/effects/branded";
import API_URL from "@utils/config";

const errorFromStatus = (status: number, message: string): LoadingErrorInput => {
    const fallback = "Invite acceptance failed.";
    const resolved = message.trim() === "" ? fallback : message;
    return toLoadingErrorInput(resolved, Option.none());
};

export class DefaultInviteAcceptClient implements InviteAcceptClient {
    constructor(private readonly markUnauthorized?: JwtSessionAction) {}

    acceptInvite(request: InviteAcceptRequest): TaskEitherTs<LoadingErrorInput, Ok> {
        const fetchTask = taskEitherTryCatch(
            () =>
                fetch(`${API_URL}${InviteAcceptRoutes.acceptInvite}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Trace-ID": uuidv7(),
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        token: request.token,
                        password: request.password,
                        ...Option.fold(request.firstName, () => ({}), (value) => ({ firstName: value })),
                        ...Option.fold(request.lastName, () => ({}), (value) => ({ lastName: value })),
                    }),
                }),
            () => toLoadingErrorInput("Network error", Option.none()),
        );

        const runUnauthorized = (): TaskEitherTs<LoadingErrorInput, Ok> => {
            if (!this.markUnauthorized) {
                return taskEitherOf(ok);
            }
            return taskEitherFromIO(ioMap(this.markUnauthorized, () => ok));
        };

        return taskEitherChain(fetchTask, (response) => {
            if (response.ok) {
                return taskEitherOf(ok);
            }

            const messageTask = taskEitherTryCatch(
                () => response.text(),
                () => toLoadingErrorInput("Invite acceptance failed.", Option.none()),
            );

            return taskEitherChain(messageTask, (message) => {
                const resolved = message || response.statusText || "Invite acceptance failed.";
                const error = errorFromStatus(response.status, resolved);
                if (response.status === 401 || response.status === 403) {
                    return taskEitherChain(runUnauthorized(), () => taskEitherLeft(error));
                }
                return taskEitherLeft(error);
            });
        });
    }
}
